import React from 'react';
import { Formik, Form } from 'formik';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonMenuButton,
  IonTitle,
  IonContent,
  IonCard,
  IonCardContent,
  IonButton,
  IonGrid,
  IonRow,
  IonCol,
  IonSpinner
} from '@ionic/react';
import alerts from 'src/lib/alerts';
import { getLocation } from 'src/lib/location';
import { isNotStarted, isCompleted } from 'src/lib/job';
import StageMilestone from 'src/components/StageMilestone';
import StageTravel from 'src/components/StageTravel';
import StageUrl from 'src/components/StageUrl';
import StageWork from 'src/components/StageWork';
import LoadingIndicator from 'src/components/LoadingIndicator';
import SuccessCheck from 'src/components/SuccessCheck';
import WithRecord from 'src/components/WithRecord';
import Subscribe from 'src/components/Subscribe';
import Field from 'src/components/Field';
import app from 'src/feathers';
import { StageContainer } from 'src/contexts';

// The container for all stage types
class Stage extends React.Component {
  constructor(props) {
    super(props);
    this.stagesService = app.service('api/stages');
    this.state = { fetching: false };

    // Create a single instance of this container and pass
    // it down as props. This allows this component and its children
    // to control the container w/o subscribing to it, while also
    // passing it down for smaller components to actually subscribe if
    // they need the time update every second. We do this because
    // it re-renders every second and we don't want to wrap
    // the whole Stage in a Subscribe and render every second, but we
    // do want to be able to call its methods.
    this.stageContainer = new StageContainer();
  }

  componentDidMount() {
    if (this.props.currentStage) {
      this.stageContainer.setStageTimer(this.props.currentStage);
    }
  }

  startStages = () => {
    return new Promise((resolve, reject) => {
      this.setState({ fetching: true }, async (newState) => {
        const latLng = await getLocation().catch((err) => {});
        this.stagesService
          .update(this.props.job._id, {
            action: 'start_stage',
            data: { start_geo: latLng }
          })
          .then((job) => {
            this.stageContainer.setStageTimer(this.props.currentStage);
            this.setState({ fetching: false }, (newState) => resolve(job));
          })
          .catch((error) => {
            this.setState({ fetching: false }, (newState) => reject(error));
          });
      });
    });
  };

  updateStage = (data) => {
    return new Promise((resolve, reject) => {
      this.setState({ fetching: true }, (newState) => {
        this.stagesService
          .update(this.props.job._id, {
            action: 'update_stage',
            data
          })
          .then((job) => {
            this.setState({ fetching: false }, (newState) => resolve(job));
          })
          .catch((error) => {
            this.setState({ fetching: false }, (newState) => reject(error));
          });
      });
    });
  };

  nextStage = async (data) => {
    return new Promise((resolve, reject) => {
      this.setState({ fetching: true }, async (newState) => {
        const latLng = await getLocation().catch((err) => {});
        this.stagesService
          .update(this.props.job._id, {
            action: 'next_stage',
            data: {
              end_geo: latLng,
              ...data
            }
          })
          .then((job) => {
            if (this.props.currentStage) {
              this.stageContainer.setStageTimer(this.props.currentStage);
            } else {
              this.stageContainer.clearStageTimer();
            }
            this.setState({ fetching: false }, (newState) => resolve(job));
          })
          .catch((error) => {
            this.setState({ fetching: false }, (newState) => reject(error));
          });
      });
    });
  };

  startBreak = async (data) => {
    return new Promise((resolve, reject) => {
      this.setState({ fetching: true }, (newState) => {
        this.stagesService
          .update(this.props.job._id, {
            action: 'start_break',
            data
          })
          .then((job) => {
            this.setState({ fetching: false }, (newState) => resolve(job));
          })
          .catch((error) => {
            alerts.errors(error);
            this.setState({ fetching: false }, (newState) => reject(error));
          });
      });
    });
  };

  endBreak = async (data) => {
    return new Promise((resolve, reject) => {
      this.setState({ fetching: true }, (newState) => {
        this.stagesService
          .update(this.props.job._id, {
            action: 'end_break',
            data
          })
          .then((job) => {
            this.setState({ fetching: false }, (newState) => resolve(job));
          })
          .catch((error) => {
            alerts.errors(error);
            this.setState({ fetching: false }, (newState) => reject(error));
          });
      });
    });
  };

  render() {
    const { job, currentStage } = this.props;
    const { fetching } = this.state;

    if (isNotStarted(job)) {
      return (
        <NotStarted
          onClick={() => this.startStages().catch(alerts.errors)}
          fetching={fetching}
        />
      );
    }

    if (isCompleted(job)) {
      return <Completed job={job} />;
    }

    const stageProps = {
      job,
      nextStage: this.nextStage,
      updateStage: this.updateStage,
      stage: currentStage,
      fetching: fetching,
      key: job.current_stage_index,
      stageContainer: this.stageContainer,
      startBreak: this.startBreak,
      endBreak: this.endBreak
    };
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/jobs" />
            </IonButtons>
            <IonButtons slot="end">
              <IonMenuButton menu="mainMenu" />
            </IonButtons>
            <IonTitle>Murphy</IonTitle>
          </IonToolbar>
        </IonHeader>
        {currentStage.type === 'milestone' && (
          <StageMilestone {...stageProps} />
        )}
        {currentStage.type === 'travel' && <StageTravel {...stageProps} />}
        {currentStage.type === 'work' && <StageWork {...stageProps} />}
        {currentStage.type === 'url' && <StageUrl {...stageProps} />}
      </IonPage>
    );
  }
}

export default (routerProps) => {
  const { _id } = routerProps.match.params;
  return (
    <WithRecord
      useLifecycle={true}
      service="api/jobs"
      _id={_id}
      render={() => {
        return (
          <Subscribe
            render={({ state }) => {
              const job = state.jobs.find((job) => job._id === _id);
              if (!job) {
                return null;
              }
              const currentStage = job.stages[job.current_stage_index];
              return (
                <Stage job={job} currentStage={currentStage} {...routerProps} />
              );
            }}
          />
        );
      }}
    />
  );
};

const NotStarted = ({ onClick, fetching }) => {
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/jobs" />
          </IonButtons>
          <IonButtons slot="end">
            <IonMenuButton menu="mainMenu" />
          </IonButtons>
          <IonTitle>Murphy</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="h-100 d-flex align-items-center">
          <div className="text-center">
            <LoadingIndicator text="Let's Go!" />
            <div className="ion-padding">
              <p className="lead mt-5 mb-5">
                Are you sure that you want to start this job? Once started, you
                will complete each stage in order and cannot go back. Any
                adjustments will need to be made by an Admin.
              </p>
              <IonButton
                onClick={onClick}
                disabled={fetching}
                expand="block"
                color="success"
              >
                {fetching ? <IonSpinner /> : 'Start Job'}
              </IonButton>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

class Completed extends React.Component {
  handleSubmit = (data, formik) => {
    app
      .service('api/jobs')
      .patch(this.props.job._id, data)
      .then(() => {
        alerts.success('Job completed!');
      })
      .catch((error) => {
        app.get('sentryClient').captureException(error);
      });
  };
  render() {
    const { job } = this.props;
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/jobs" />
            </IonButtons>
            <IonButtons slot="end">
              <IonMenuButton menu="mainMenu" />
            </IonButtons>
            <IonTitle>Murphy</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <IonGrid className="pt-3">
            <IonCard>
              <IonCardContent>
                <IonRow className="justify-content-center">
                  <IonCol size="7">
                    <SuccessCheck />
                  </IonCol>
                </IonRow>
                <div className="text-center">
                  <h1 className="animated fadeIn mt-0">Nice Work!</h1>
                  <h2 className="animated fadeIn">
                    You have completed all stages.
                  </h2>
                </div>
                {!job.field_notes && (
                  <Formik
                    initialValues={{ field_notes: '' }}
                    onSubmit={this.handleSubmit}
                    render={({ isSubmitting }) => {
                      return (
                        <Form>
                          <Field
                            type="textarea"
                            name="field_notes"
                            rows="3"
                            placeholder="Add some closing notes to the job. Is there anything you would like to document?"
                            disabled={isSubmitting}
                          />
                          <IonButton
                            type="submit"
                            expand="block"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? <IonSpinner /> : 'Add Notes'}
                          </IonButton>
                        </Form>
                      );
                    }}
                  />
                )}
                <div className="d-flex justify-content-between mt-3">
                  <IonButton
                    routerLink={`/jobs/${job._id}/stats`}
                    color="medium"
                  >
                    View Stats
                  </IonButton>
                  <IonButton routerLink="/jobs" color="medium">
                    View More Jobs
                  </IonButton>
                </div>
              </IonCardContent>
            </IonCard>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }
}
